/**
 * PIN Code Security Service
 * Handles PIN creation, verification, and storage
 */

import { sha256 } from '../crypto/hash';

export interface PinSettings {
  enabled: boolean;
  hash: string;
  salt: string;
  attempts: number;
  maxAttempts: number;
  lockoutUntil?: number;
  createdAt: number;
  lastChanged: number;
}

class PinCodeService {
  private readonly STORAGE_KEY_PREFIX = 'pin_settings_';
  private readonly MAX_ATTEMPTS = 5;
  private readonly LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes

  /**
   * Set up PIN code for user
   */
  async setupPin(userId: string, pin: string): Promise<{ success: boolean; error?: string }> {
    // Validate PIN
    if (!this.validatePin(pin)) {
      return {
        success: false,
        error: 'PIN must be 4-6 digits',
      };
    }

    try {
      // Generate salt
      const salt = this.generateSalt();
      
      // Hash PIN with salt
      const hash = await this.hashPin(pin, salt);

      // Create settings
      const settings: PinSettings = {
        enabled: true,
        hash,
        salt,
        attempts: 0,
        maxAttempts: this.MAX_ATTEMPTS,
        createdAt: Date.now(),
        lastChanged: Date.now(),
      };

      // Save to storage
      this.saveSettings(userId, settings);

      return { success: true };
    } catch (error) {
      console.error('Failed to setup PIN:', error);
      return {
        success: false,
        error: 'Failed to set up PIN code',
      };
    }
  }

  /**
   * Verify PIN code
   */
  async verifyPin(userId: string, pin: string): Promise<{ success: boolean; error?: string; attemptsRemaining?: number }> {
    const settings = this.getSettings(userId);
    
    if (!settings || !settings.enabled) {
      return {
        success: false,
        error: 'PIN not set up',
      };
    }

    // Check if account is locked
    if (settings.lockoutUntil && Date.now() < settings.lockoutUntil) {
      const remainingMinutes = Math.ceil((settings.lockoutUntil - Date.now()) / 60000);
      return {
        success: false,
        error: `Account locked. Try again in ${remainingMinutes} minutes.`,
      };
    }

    // Validate PIN format
    if (!this.validatePin(pin)) {
      return {
        success: false,
        error: 'Invalid PIN format',
      };
    }

    try {
      // Hash entered PIN with stored salt
      const hash = await this.hashPin(pin, settings.salt);

      // Compare hashes
      if (hash === settings.hash) {
        // Success - reset attempts
        settings.attempts = 0;
        delete settings.lockoutUntil;
        this.saveSettings(userId, settings);
        
        return { success: true };
      } else {
        // Failed attempt
        settings.attempts++;
        
        // Check if max attempts reached
        if (settings.attempts >= settings.maxAttempts) {
          settings.lockoutUntil = Date.now() + this.LOCKOUT_DURATION;
          this.saveSettings(userId, settings);
          
          return {
            success: false,
            error: `Too many failed attempts. Account locked for ${this.LOCKOUT_DURATION / 60000} minutes.`,
            attemptsRemaining: 0,
          };
        }
        
        this.saveSettings(userId, settings);
        
        const remaining = settings.maxAttempts - settings.attempts;
        return {
          success: false,
          error: 'Incorrect PIN',
          attemptsRemaining: remaining,
        };
      }
    } catch (error) {
      console.error('PIN verification error:', error);
      return {
        success: false,
        error: 'Failed to verify PIN',
      };
    }
  }

  /**
   * Change PIN code
   */
  async changePin(userId: string, oldPin: string, newPin: string): Promise<{ success: boolean; error?: string }> {
    // Verify old PIN first
    const verifyResult = await this.verifyPin(userId, oldPin);
    if (!verifyResult.success) {
      return {
        success: false,
        error: verifyResult.error || 'Old PIN is incorrect',
      };
    }

    // Validate new PIN
    if (!this.validatePin(newPin)) {
      return {
        success: false,
        error: 'New PIN must be 4-6 digits',
      };
    }

    // Don't allow same PIN
    if (oldPin === newPin) {
      return {
        success: false,
        error: 'New PIN must be different from old PIN',
      };
    }

    // Set up new PIN
    return await this.setupPin(userId, newPin);
  }

  /**
   * Remove PIN code
   */
  removePin(userId: string): void {
    localStorage.removeItem(this.STORAGE_KEY_PREFIX + userId);
  }

  /**
   * Alias for removePin - clears PIN for user
   */
  clearPin(userId: string): void {
    this.removePin(userId);
  }

  /**
   * Set/update PIN code (wrapper for setupPin)
   */
  async setPin(userId: string, pin: string): Promise<{ success: boolean; error?: string }> {
    return await this.setupPin(userId, pin);
  }

  /**
   * Check if PIN is enabled for user
   */
  isPinEnabled(userId: string): boolean {
    const settings = this.getSettings(userId);
    return !!settings?.enabled;
  }

  /**
   * Get PIN settings
   */
  getPinInfo(userId: string): { enabled: boolean; attempts: number; maxAttempts: number; isLocked: boolean; lockoutMinutes?: number } {
    const settings = this.getSettings(userId);
    
    if (!settings) {
      return {
        enabled: false,
        attempts: 0,
        maxAttempts: this.MAX_ATTEMPTS,
        isLocked: false,
      };
    }

    const isLocked = !!(settings.lockoutUntil && Date.now() < settings.lockoutUntil);
    const lockoutMinutes = isLocked
      ? Math.ceil((settings.lockoutUntil! - Date.now()) / 60000)
      : undefined;

    return {
      enabled: settings.enabled,
      attempts: settings.attempts,
      maxAttempts: settings.maxAttempts,
      isLocked,
      lockoutMinutes,
    };
  }

  /**
   * Reset failed attempts (admin use)
   */
  resetAttempts(userId: string): void {
    const settings = this.getSettings(userId);
    if (settings) {
      settings.attempts = 0;
      delete settings.lockoutUntil;
      this.saveSettings(userId, settings);
    }
  }

  // Helper methods
  private validatePin(pin: string): boolean {
    return /^\d{4,6}$/.test(pin);
  }

  private generateSalt(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  private async hashPin(pin: string, salt: string): Promise<string> {
    return await sha256(pin + salt);
  }

  private saveSettings(userId: string, settings: PinSettings): void {
    localStorage.setItem(
      this.STORAGE_KEY_PREFIX + userId,
      JSON.stringify(settings)
    );
  }

  private getSettings(userId: string): PinSettings | null {
    const stored = localStorage.getItem(this.STORAGE_KEY_PREFIX + userId);
    if (!stored) return null;
    
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
}

export const pinCodeService = new PinCodeService();
